import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, Star, Crown } from "lucide-react";
import { useLang } from "@/context/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = { Package, Star, Crown };

export default function Packages() {
  const { t } = useLang();
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    axios.get(`${API}/content/packages`).then(r => setPackages(r.data)).catch(() => {});
  }, []);

  const scrollTo = () => document.querySelector("#soumission")?.scrollIntoView({ behavior: "smooth" });

  if (!packages.length) return null;

  return (
    <section id="forfaits" className="relative py-16 md:py-32 bg-[#0a0a0a]" data-testid="packages-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#E60000]/5 rounded-full blur-[150px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        <div className="mb-10 md:mb-16 text-center">
          <span className="text-[#E60000] text-xs sm:text-sm uppercase tracking-[0.3em] font-medium font-body">{t.packages.section_tag}</span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold uppercase text-white mt-3" data-testid="packages-title">
            {t.packages.title} <span className="text-[#E60000]">{t.packages.title_accent}</span>
          </h2>
          <p className="font-body text-[#A3A3A3] mt-4 max-w-2xl mx-auto">{t.packages.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg, i) => {
            const Icon = iconMap[pkg.icon] || Package;
            return (
              <motion.div key={pkg.id || i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.6 }} className={`relative p-8 border transition-all duration-500 group hover:translate-y-[-4px] ${pkg.accent ? "border-[#E60000]/60 bg-[#E60000]/5 hover:shadow-2xl hover:shadow-red-900/30" : "border-white/10 bg-[#121212]/50 hover:border-[#E60000]/40"}`} data-testid={`package-${i}`}>
                {pkg.accent && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E60000] text-white text-[10px] font-bold uppercase tracking-[0.3em] px-4 py-1">{t.packages.popular}</div>}
                <Icon className={`w-8 h-8 mb-4 ${pkg.accent ? "text-[#E60000]" : "text-white/40"}`} />
                <h3 className="font-heading text-2xl font-bold uppercase text-white mb-1 tracking-tight">{pkg.title}</h3>
                <p className="font-heading text-lg text-[#E60000] font-bold mb-3">{pkg.price}</p>
                <p className="font-body text-sm text-[#A3A3A3] mb-6">{pkg.description}</p>
                <ul className="space-y-2.5 mb-8">
                  {(pkg.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5"><div className="w-1.5 h-1.5 rounded-full bg-[#E60000] mt-1.5 shrink-0" /><span className="font-body text-sm text-white/70">{f}</span></li>
                  ))}
                </ul>
                <button onClick={scrollTo} className={`w-full text-sm font-bold uppercase tracking-[0.15em] py-3.5 transition-all duration-300 ${pkg.accent ? "bg-[#E60000] hover:bg-[#FF3333] text-white" : "border border-white/20 text-white hover:border-[#E60000] hover:bg-[#E60000]/10"}`} data-testid={`package-cta-${i}`}>
                  {t.packages.cta}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
